import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, Star, Play, X } from "lucide-react";
import { Button } from "../ui/button";
import { heroSlides, videos } from "../../mock/mock";

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrent((c) => (c + 1) % heroSlides.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const slide = heroSlides[current];
  const heroVideo = videos[0];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Decorative shapes */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-300/40 blob-shape animate-float hidden md:block" />
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-orange-300/40 rounded-full animate-float hidden md:block" style={{ animationDelay: "1s" }} />
      <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-red-300/40 rounded-full animate-float hidden md:block" style={{ animationDelay: "2s" }} />

      <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="section-fade-in" key={current}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 border border-orange-200 mb-6">
              <Sparkles className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-semibold text-orange-800 tracking-wide uppercase">{slide.cta}</span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 leading-tight mb-4">
              {slide.title}{" "}
              <span className="bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 bg-clip-text text-transparent">
                {slide.highlight}
              </span>
            </h1>

            <p className="text-lg text-stone-600 mb-4 max-w-lg leading-relaxed">{slide.subtitle}</p>

            <p className="font-devanagari text-2xl text-orange-700 italic mb-8">संस्कारेण प्रगतिः <span className="text-base text-stone-500 not-italic">• Progress through Sanskaar</span></p>

            <div className="flex flex-wrap gap-4">
              <Link to="/admission">
                <Button size="lg" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-full px-8 h-12 shadow-lg btn-shine">
                  Enroll Your Child <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button size="lg" variant="outline" className="border-2 border-orange-600 text-orange-700 hover:bg-orange-50 rounded-full px-8 h-12">
                  View Programs
                </Button>
              </Link>
              <button
                onClick={() => setShowVideo(true)}
                className="flex items-center gap-2.5 text-stone-700 hover:text-orange-700 font-semibold group"
              >
                <span className="w-11 h-11 rounded-full bg-white border-2 border-orange-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 text-orange-600 fill-orange-600 ml-0.5" />
                </span>
                Watch Video
              </button>
            </div>

            {/* slide indicators */}
            <div className="flex gap-2 mt-10">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${i === current ? "w-10 bg-orange-600" : "w-4 bg-orange-200"}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Image side */}
          <div className="relative">
            <div className="relative aspect-[3/4] max-w-md mx-auto">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-amber-500 blob-shape animate-wobble" />
              <img
                src={slide.image}
                alt="Aadi Gayanam Play School"
                className="relative w-full h-full object-cover blob-shape shadow-2xl"
                style={{ objectPosition: "center top" }}
              />
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-2 animate-float hidden md:flex">
                <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
                <div>
                  <p className="text-xs text-stone-500">Rated</p>
                  <p className="text-sm font-bold text-stone-800">4.9/5</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 animate-float hidden md:block" style={{ animationDelay: "1.5s" }}>
                <p className="text-xs text-stone-500">Launching</p>
                <p className="font-display font-bold text-xl text-orange-700">Jan 2027</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <svg className="w-full h-16 md:h-24 -mb-1" viewBox="0 0 1440 100" preserveAspectRatio="none">
        <path d="M0,50 C240,100 480,0 720,50 C960,100 1200,0 1440,50 L1440,100 L0,100 Z" fill="#FFFBF3" />
      </svg>

      {/* Video modal */}
      {showVideo && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <button
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40"
            onClick={() => setShowVideo(false)}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video bg-stone-900 rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${heroVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                title={heroVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
